import {
  Box,
  Button,
  Checkbox,
  Grid,
  HStack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Card, InputDatePicker, Select } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import type { CreateOrderMutation } from "@src/__generated__/graphql";
import { DocumentSequenceAutocompleteField } from "@src/components/ui-kit/AutocompleteField/variants/invoice-sequence";
import { Icon } from "@src/components/ui-kit/Icon";
import { LargeSelect } from "@src/components/ui-kit/Select/LargeSelect";
import { ModalOrder } from "@src/components/widgets/Modals/ModalOrder";
import { can, cannot } from "@src/utils/components/permissions";
import type { DateRange as Range } from "@src/utils/types";
import { addDays } from "date-fns";
import { observer } from "mobx-react-lite";
import type { FC, PropsWithChildren } from "react";
import { InvoiceTypeEnum, useInvoiceStore } from "../../../store";

const Row: FC<PropsWithChildren> = ({ children }) => {
  return (
    <HStack
      align="center"
      justify="space-between"
      display="flex"
      w="full"
      minH="3.8rem"
    >
      {children}
    </HStack>
  );
};

const Section: FC<
  PropsWithChildren & {
    rightPadding?: boolean;
  }
> = ({ children, rightPadding }) => {
  return (
    <VStack
      as="section"
      display="flex"
      pr={rightPadding ? "12" : undefined}
      spacing="3"
    >
      {children}
    </VStack>
  );
};

export const InvoiceDetailsCard = observer(function InvoiceDetailsCard() {
  const store = useInvoiceStore();
  const modalCreateOrder = useDisclosure();

  const onSelectIssueDate = (date: Range | undefined) => {
    store.form.issue_date.onChange(date?.start);
    if (!store.form.date_of_supply.value) {
      store.form.date_of_supply.onChange(date?.start);
      if (store.type !== InvoiceTypeEnum.Proforma) {
        store.form.invoice_no.reset();
        store.form.invoice_order_sequence_id.reset();
        store.fetchInvoiceNo(
          date?.start,
          store.form.workspace_company_id.value,
          store.invoiceSequenceType,
        );
      }
    }

    if (!date?.start) return;
    const paymentDue = store.selectedBillingEntity?.payment_due || 14;
    const dueDate = addDays(date.start, paymentDue);
    store.form.due_date.onChange(dueDate);

    if (store.type !== InvoiceTypeEnum.Proforma || !store.isRFI) return;
    store.fetchInvoiceNo(
      date.start,
      store.form.workspace_company_id.value,
      store.invoiceSequenceType,
    );
  };

  const onCreateOrder = (order: CreateOrderMutation["createOrder"]) => {
    if (!order) return;

    store.addOrderOption(order);
    store.form.addOrder(order.id);
    if (order.pdfFiles)
      store.form.addMedia(order.pdfFiles.map((i) => i.public_id));

    store.documentsRefetch?.();
    store.selectedProjectsRefetch?.();
  };

  const onSelectOrder = (value: string[]) => {
    store.form.order_ids.onChange(value);

    const media_ids = store.ordersWithPdf
      .filter((i) => value.includes(i.id) && i.pdfFiles.length)
      .flatMap((i) => i.pdfFiles.map((j) => j.id));
    store.form.addMedia(media_ids);
  };

  const firstColumnHasAnyElement =
    store.isDueDateRequired ||
    store.isIssueDateRequired ||
    store.isDateOfSupplyRequired;

  return (
    <Box mb="5">
      <Card sectioned>
        <Text as="h2" mb="5" fontSize="lg" fontWeight="semibold">
          {store.type === InvoiceTypeEnum.CreditNote
            ? t`Credit note details`
            : t`Invoice details`}
        </Text>
        <Grid
          gap="12"
          templateColumns={{
            base: "1fr",
            md: "1fr 1fr",
          }}
        >
          {firstColumnHasAnyElement && (
            <Section>
              {store.isIssueDateRequired && (
                <Row>
                  <Trans>Issue date</Trans>
                  <Box w="50%">
                    <InputDatePicker
                      selected={store.form.issue_date.value}
                      onChange={onSelectIssueDate}
                      error={store.form.issue_date.error}
                    />
                  </Box>
                </Row>
              )}
              {store.isDateOfSupplyRequired && (
                <Row>
                  {store.isFinancier ? (
                    <Trans>Date of supply</Trans>
                  ) : (
                    <Trans>Requested date of supply</Trans>
                  )}
                  <Box w="50%">
                    <InputDatePicker
                      selected={store.form.date_of_supply.$}
                      onChange={store.handleDateOfSupplyChange}
                      error={store.form.date_of_supply.error}
                    />
                  </Box>
                </Row>
              )}
              {store.isDueDateRequired && (
                <Row>
                  <Trans>Due date</Trans>
                  <Box w="50%">
                    <InputDatePicker
                      selected={store.form.due_date.value}
                      onChange={(val) =>
                        store.form.due_date.onChange(val?.start)
                      }
                      error={store.form.due_date.error}
                    />
                  </Box>
                </Row>
              )}
              <Row>
                <Trans>Invoice no</Trans>
                <Box w="50%">
                  <DocumentSequenceAutocompleteField
                    isLoading={store.isInvoiceNoFetching}
                    autoCompleteOptions={store.invoiceNoOptions}
                    isDisabled={cannot("outgoingInvoice_updateInvoiceNumber")}
                    value={store.form.invoice_no.value ?? ""}
                    onChange={store.handleChangeInvoiceNo}
                    onSelect={store.handleSelectInvoiceNo}
                    error={
                      store.form.invoice_no.error ||
                      store.form.invoice_order_sequence_id.error
                    }
                    placeholder={t`Invoice no`}
                    onPopoverOpen={store.handleInvoiceAutocompletePopoverOpen}
                    noOptionsMessage={store.noSequenceOptionsMessage}
                  />
                </Box>
              </Row>
            </Section>
          )}

          <Section rightPadding={!firstColumnHasAnyElement}>
            <Row>
              <Trans>Currency</Trans>
              <Box w="110px">
                <Select
                  value={store.form.currency_id.value}
                  onChange={store.onSelectCurrency}
                  options={store.currenciesOptions}
                />
              </Box>
            </Row>
            {store.showVat && (
              <Row>
                <Trans>VAT</Trans>
                <Box w="110px">
                  <Select
                    isDisabled={
                      !store.isFinancier || store.form.vat_reverse_charge.value
                    }
                    value={store.form.vat_percent_informative.value}
                    onChange={store.form.handleChangeGlobalVat}
                    options={store.vatRatesOptions}
                    error={store.form.vat_percent_informative.error}
                  />
                </Box>
              </Row>
            )}
            <Row>
              <Trans>Payment method</Trans>
              <Box w="50%">
                <Select
                  placeholder={t`Select payment method`}
                  value={store.form.invoicePaymentMethodId.value}
                  onChange={store.form.invoicePaymentMethodId.onChange}
                  options={store.invoicePaymentMethodOptions}
                />
              </Box>
            </Row>
            {store.showVat &&
              store.hasDifferentBillingAndLegalEntityCountry && (
                <Row>
                  <Trans>Reverse charge</Trans>
                  <HStack justify="end" w="110px">
                    <Checkbox
                      isChecked={store.form.vat_reverse_charge.value}
                      isDisabled={!store.isFinancier}
                      onChange={(e) =>
                        store.handleVatReverseChargeChange(e.target.checked)
                      }
                    />
                  </HStack>
                </Row>
              )}
            {store.type !== InvoiceTypeEnum.Proforma && (
              <Row>
                <Trans>Purchase order number (PO)</Trans>
                <Box w="200px">
                  <LargeSelect
                    placeholder={t`Select PO`}
                    isMulti
                    isSearchable
                    value={store.form.order_ids.value}
                    onChange={onSelectOrder}
                    options={store.orderOptions}
                    loadOptions={store.fetchOrders}
                    topExtraContent={
                      can("order_create") && (
                        <Button
                          leftIcon={<Icon name="plus" />}
                          onClick={modalCreateOrder.onOpen}
                          variant="link"
                        >
                          <Trans>Add new PO</Trans>
                        </Button>
                      )
                    }
                  />
                </Box>
                <ModalOrder
                  options={{
                    brand: store.selectedBrandsOptions,
                    project: store.selectedProjectsOptions,
                  }}
                  prefillFields={{
                    brandId: store.form.brand_ids.value[0],
                    projectIds: store.form.project_ids.value,
                  }}
                  onSubmit={onCreateOrder}
                  {...modalCreateOrder}
                />
              </Row>
            )}
            {store.type === InvoiceTypeEnum.Outgoing && (
              <Row>
                <Trans>Attach proforma invoice</Trans>
                <Box w="250px">
                  <Select
                    placeholder={t`Select proforma invoice`}
                    isMulti
                    isSearchable
                    value={store.form.outgoing_proforma_invoice_ids.value}
                    onChange={store.handleOutgoingProformaInvoiceIdsChange}
                    error={store.form.outgoing_proforma_invoice_ids.error}
                    options={store.proformaInvoicesOptions}
                  />
                </Box>
              </Row>
            )}

            {store.type !== InvoiceTypeEnum.Proforma &&
              store.form.outgoing_proforma_invoice_ids.value.length > 0 && (
                <Row>
                  <Trans>Attach payment receipt</Trans>
                  <Box w="250px">
                    <Select
                      placeholder={t`Select payment receipt`}
                      isMulti
                      isSearchable
                      options={store.paymentReceiptOptions}
                      onChange={store.form.payment_receipt_ids.onChange}
                      value={store.form.payment_receipt_ids.value}
                      error={store.form.payment_receipt_ids.error}
                    />
                  </Box>
                </Row>
              )}
          </Section>
        </Grid>
      </Card>
    </Box>
  );
});
